import { AggregationFn } from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';

export function areAllStringsEqual(strings: string[]): boolean {
  if (strings.length === 0) {
    return true;
  }

  const firstString = strings[0];

  for (const str of strings) {
    if (str !== firstString) {
      return false;
    }
  }

  return true;
}

export const aggregateGreekSum: AggregationFn<PositionView> = (
  columnId,
  leafRows,
) => {
  const productCodes = leafRows.map((r) => r.original.productCode);
  const isProductCodeEqual = areAllStringsEqual(productCodes);

  const [firstElement] = leafRows;
  if (!isProductCodeEqual || firstElement == null) return null;
  const firstRow = firstElement.original;

  const isNull = leafRows.every((row) => {
    const { greeks } = row.original;
    const rawValue = greeks[columnId as keyof typeof greeks];
    const { value } = rawValue;
    return value == null;
  });

  if (isNull) return NEGATIVE_SIGN;

  const sum = leafRows.reduce((acc, row) => {
    const { greeks } = row.original;
    const rawValue = greeks[columnId as keyof typeof greeks];
    const { value } = rawValue;
    return value == null ? acc : acc.plus(value);
  }, new BigNumber(0));

  const { formattingFn } =
    firstRow.greeks[columnId as keyof typeof firstRow.greeks];

  return formattingFn(sum);
};

export const aggregatedQuantitySum: AggregationFn<PositionView> = (
  _,
  leafRows,
) => {
  const clearingCurrencies = leafRows.map((r) => r.original.clearing_currency);
  const isClearingCurrencyEqual = areAllStringsEqual(clearingCurrencies);
  const [firstElement] = leafRows;
  if (!isClearingCurrencyEqual || !firstElement) return null;

  const sum = leafRows.reduce((acc, row) => {
    return acc.plus(row.original.quantity.value);
  }, new BigNumber(0));

  const firstRow = firstElement.original;
  const { formattingFn } = firstRow.quantity;
  return formattingFn(sum);
};

export const aggregatedUnrealizedPnl: AggregationFn<PositionView> = (
  _,
  leafRows,
) => {
  const [firstElement] = leafRows;
  if (!firstElement) return null;

  const sum = leafRows.reduce((acc, row) => {
    return acc.plus(row.original.unrealizedPnl.value);
  }, new BigNumber(0));

  const firstRow = firstElement.original;
  const { getColor, formattingFn } = firstRow.unrealizedPnl;
  return {
    color: getColor(sum),
    formatted: formattingFn(sum),
    value: sum,
  };
};
